import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  HostListener
} from '@angular/core';
import { EvolucionTipoVisitaMes } from '../../services/dashboard.service';
import {
  AnclaDomGrafico,
  cerrarOverlayPantallaCompleta,
  sincronizarOverlayPantallaCompleta
} from '../../utils/grafico-fullscreen.util';
import {
  CHART_DASHBOARD_ANIMACION_ENTRADA_MS,
  CHART_DASHBOARD_OPCIONES_ANIMACION_ENTRADA
} from '../../config/chart-dashboard.config';
import { SERIES_EVOLUCION_TIPOS_VISITA } from '../../utils/tipo-visita.utils';
import {
  ColumnaTablaFullscreen,
  construirTablaEvolucion
} from '../../utils/grafico-fullscreen-tabla.util';

type Chart = any;

@Component({
  selector: 'app-grafico-evolucion-visitas',
  templateUrl: './grafico-evolucion-visitas.component.html',
  styleUrls: ['./grafico-evolucion-visitas.component.scss'],
  standalone: false
})
export class GraficoEvolucionVisitasComponent implements AfterViewChecked, OnChanges, OnDestroy {
  private static contadorId = 0;
  public idUnico: string;

  @ViewChild('graficoCanvas', { static: false }) graficoCanvasRef!: ElementRef;
  @ViewChild('pantallaCompletaRoot', { static: false }) pantallaCompletaRoot!: ElementRef<HTMLElement>;

  @Input() titulo: string = 'Tipos de Visita por Estado';
  @Input() subtitulo: string = 'Cantidad de visitas por mes según tipo y estado';
  @Input() datos: EvolucionTipoVisitaMes[] = [];
  @Input() periodoSeleccionado: number | null = null;
  @Input() estadoSeleccionado: number | null = null;
  @Input() cargando: boolean = false;
  @Input() informacion: string = '';

  @Output() periodoCambiado = new EventEmitter<number>();
  @Output() estadoCambiado = new EventEmitter<number>();

  modalAbierto = false;

  public opcionesEstado: { valor: number; label: string }[] = [
    { valor: 2, label: 'Finalizada' },
    { valor: 1, label: 'Agendada' },
    { valor: 4, label: 'Planificada' }
  ];

  public opcionesPeriodo: { valor: number; label: string }[] = [
    { valor: 3, label: 'Últimos 3 meses' },
    { valor: 6, label: 'Últimos 6 meses' },
    { valor: 12, label: 'Último año' }
  ];


  public opcionesSelectEstadoModal = {
    cssClass: 'select-popover select-popover-checkmark',
    showBackdrop: true,
    backdropDismiss: true
  };

  public opcionesSelectPeriodoModal = {
    cssClass: 'select-popover select-popover-checkmark',
    showBackdrop: true,
    backdropDismiss: true
  };



  graficoListo = false;
  columnasTablaFullscreen: ColumnaTablaFullscreen[] = [];
  filasTablaFullscreen: Record<string, string | number>[] = [];

  private grafico: Chart | null = null;
  private graficoCreado = false;
  private creandoGrafico = false;
  private chartJsCargado = false;
  private ChartClass: any = null;
  private _periodoSeleccionadoInterno: number | null = null;
  private _estadoSeleccionadoInterno: number | null = null;


  private resizeObserver: ResizeObserver | null = null;
  private resizeFrameId: number | null = null;
  private animacionEntradaEnCurso = false;
  private animacionEntradaTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private ultimoTamanoContenedor: { w: number; h: number } | null = null;
  private anclaDomOverlay: AnclaDomGrafico | null = null;

  constructor() {
    this.idUnico = `informacion-grafico-evolucion-${GraficoEvolucionVisitasComponent.contadorId++}`;
  }

  ngAfterViewChecked(): void {
    if (this.modalAbierto) {
      this.anclaDomOverlay = sincronizarOverlayPantallaCompleta(
        this.pantallaCompletaRoot?.nativeElement,
        true,
        this.anclaDomOverlay
      );
    }

    if (
      !this.graficoCreado &&
      !this.creandoGrafico &&
      this.graficoCanvasRef &&
      this.datos.length > 0 &&
      !this.cargando
    ) {
      this.creandoGrafico = true;
      void this.crearGrafico()
        .then(() => this.finalizarRenderGrafico())
        .catch(console.error)
        .finally(() => {
          this.creandoGrafico = false;
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cargando'] && this.cargando) {
      if (this.datos.length === 0) {
        this.graficoCreado = false;
        this.graficoListo = false;
        this.creandoGrafico = false;
        this.destruirGrafico();
      }
      if (!this.modalAbierto) {
        this._periodoSeleccionadoInterno = null;
        this._estadoSeleccionadoInterno = null;
      }

      return;
    }

    if (changes['datos'] || changes['periodoSeleccionado'] || (changes['cargando'] && !this.cargando)) {
      this.actualizarTablaFullscreen();
      if (this.datos.length === 0) {
        this.graficoCreado = false;
        this.graficoListo = false;
        this.destruirGrafico();
        return;
      }

      if (this.grafico) {
        void this.actualizarGrafico().then(() => {
          if (this.modalAbierto) {
            this.programarResizeGrafico(true);
          }
        });
      } else if (this.graficoCreado) {
        this.graficoCreado = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.destruirGrafico();
    if (this.modalAbierto) {
      this.cerrarPantallaCompleta();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.modalAbierto) {
      this.cerrarPantallaCompleta();
    }
  }

  private actualizarTablaFullscreen(): void {
    const tabla = construirTablaEvolucion(this.datos);
    this.columnasTablaFullscreen = tabla.columnas;
    this.filasTablaFullscreen = tabla.filas;
  }

  get periodoActual(): number {
    if (this._periodoSeleccionadoInterno !== null) {
      return this._periodoSeleccionadoInterno;
    }
    if (this.periodoSeleccionado !== null) {
      return this.periodoSeleccionado;
    }
    return 6;
  }

  get estadoActual(): number {
    if (this._estadoSeleccionadoInterno !== null) {
      return this._estadoSeleccionadoInterno;
    }
    if (this.estadoSeleccionado !== null) {
      return this.estadoSeleccionado;
    }
    return 2;
  }

  onEstadoChange(event: CustomEvent): void {
    const nuevoEstado = Number(event.detail.value);
    if (!Number.isFinite(nuevoEstado)) {
      return;
    }
    this._estadoSeleccionadoInterno = nuevoEstado;
    this.estadoCambiado.emit(nuevoEstado);
  }


  abrirPantallaCompleta(): void {
    if (this.cargando || this.datos.length === 0 || this.modalAbierto) {
      return;
    }
    this.actualizarTablaFullscreen();
    this.modalAbierto = true;
    this.programarResizeGrafico(true);
  }

  cerrarPantallaCompleta(): void {
    if (!this.modalAbierto) {
      return;
    }
    this.modalAbierto = false;
    cerrarOverlayPantallaCompleta(this.pantallaCompletaRoot?.nativeElement, this.anclaDomOverlay);
    this.anclaDomOverlay = null;
    this.programarResizeGrafico(true);
  }

  onPeriodoChange(event: CustomEvent): void {
    const nuevoPeriodo = Number(event.detail.value);
    if (!Number.isFinite(nuevoPeriodo)) {
      return;
    }
    this._periodoSeleccionadoInterno = nuevoPeriodo;
    this.periodoCambiado.emit(nuevoPeriodo);
  }

  private luminanciaHex(hex: string): number {
    const n = parseInt(hex.replace(/^#/, ''), 16);
    const r = (n >> 16) / 255;
    const g = ((n >> 8) & 0xff) / 255;
    const b = (n & 0xff) / 255;
    const [R, G, B] = [r, g, b].map(c =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    );
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  }

  private coloresEtiquetaSobreFondo(hexFondo: string): { color: string; textStrokeColor: string } {
    const L = this.luminanciaHex(hexFondo || '#6b7280');
    const umbral = 0.45;
    if (L < umbral) {
      return { color: '#ffffff', textStrokeColor: 'rgba(0, 0, 0, 0.35)' };
    }
    return { color: '#1a1a1a', textStrokeColor: 'rgba(255, 255, 255, 0.85)' };
  }

  private async cargarChartJs(): Promise<void> {
    if (this.chartJsCargado && this.ChartClass) return;

    const [{ Chart, registerables }, ChartDataLabels] = await Promise.all([
      import('chart.js'),
      import('chartjs-plugin-datalabels')
    ]);

    Chart.register(...registerables, ChartDataLabels.default);
    this.ChartClass = Chart;
    this.chartJsCargado = true;
  }

  private obtenerConfigDatasets() {
    return SERIES_EVOLUCION_TIPOS_VISITA;
  }

  private construirConfiguracionGrafico() {
    const labels = this.datos.map(m => m.label) || [];
    const datasetsConfig = this.obtenerConfigDatasets();

    return {
      type: 'bar' as const,
      data: {
        labels,
        datasets: datasetsConfig.map(cfg => ({
          label: cfg.label,
          data: this.datos.map(m => m[cfg.campo]),
          backgroundColor: cfg.color,
          borderColor: cfg.color,
          borderWidth: 1,
          borderRadius: 2
        }))
      },
      options: {
        animation: CHART_DASHBOARD_OPCIONES_ANIMACION_ENTRADA,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 4, right: 4, bottom: 4, left: 4 }
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom' as const,
            align: 'center' as const,
            labels: {
              usePointStyle: true,
              pointStyle: 'circle' as const,
              boxWidth: 8,
              boxHeight: 8,
              font: { size: 11 },
              padding: 6
            }
          },
          datalabels: {
            display: (context: any) => {
              const value = context.dataset.data[context.dataIndex];
              return Number(value) > 0;
            },
            anchor: 'center' as const,
            align: 'center' as const,
            color: (context: any) => {
              const bg = context.dataset.backgroundColor;
              return this.coloresEtiquetaSobreFondo(bg).color;
            },
            textStrokeColor: (context: any) => {
              const bg = context.dataset.backgroundColor;
              return this.coloresEtiquetaSobreFondo(bg).textStrokeColor;
            },
            textStrokeWidth: 1.5,
            font: { weight: 'bold' as const, size: 12 },
            formatter: (value: number) => (Number(value) > 0 ? String(value) : '')
          }
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              maxRotation: 45,
              minRotation: 0,
              font: { size: 11, weight: '500' as const },
              color: '#333'
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grace: '0%' as const,
            grid: { display: false },
            title: {
              display: true,
              text: 'Cantidad de Visitas',
              font: { size: 12, weight: '600' as const },
              color: '#1a1a1a'
            },
            ticks: {
              stepSize: 1,
              font: { size: 11, weight: '500' as const },
              color: '#333'
            }
          }
        }
      }
    };
  }

  private async crearGrafico(): Promise<void> {
    const ctx = this.graficoCanvasRef?.nativeElement?.getContext('2d');
    if (!ctx) return;

    await this.cargarChartJs();

    if (!this.ChartClass) {
      console.error('Chart.js no se pudo cargar');
      return;
    }

    this.destruirGrafico();
    this.iniciarAnimacionEntrada();

    const config = this.construirConfiguracionGrafico();
    const chart = new this.ChartClass(ctx, config);
    this.grafico = chart;

    const canvas = this.graficoCanvasRef.nativeElement;
    const contenedor = canvas.closest('.grafico-contenedor') as HTMLElement | null;
    const elementoObservado = contenedor ?? canvas.parentElement;

    if (elementoObservado && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver?.disconnect();

      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const { width, height } = entry.contentRect;
        const prev = this.ultimoTamanoContenedor;
        if (prev && Math.abs(prev.w - width) < 2 && Math.abs(prev.h - height) < 2) {
          return;
        }
        this.ultimoTamanoContenedor = { w: width, h: height };
        this.programarResizeGrafico();
      });
      this.resizeObserver.observe(elementoObservado);
    }
  }

  private async actualizarGrafico(): Promise<void> {
    if (!this.grafico) {
      await this.crearGrafico();
      this.finalizarRenderGrafico();
      return;
    }

    if (this.datos && this.datos.length > 0) {
      const configs = this.obtenerConfigDatasets();
      this.grafico.data.labels = this.datos.map(m => m.label);
      configs.forEach((cfg, i) => {
        this.grafico.data.datasets[i].data = this.datos.map(m => m[cfg.campo]);
      });
    } else {
      this.grafico.data.labels = [];
      this.grafico.data.datasets.forEach((ds: { data: unknown[] }) => {
        ds.data = [];
      });
    }

    this.grafico.update('none');
    this.programarResizeGrafico(this.modalAbierto);
  }

  private finalizarRenderGrafico(): void {
    this.graficoCreado = true;
    this.graficoListo = true;
  }

  private iniciarAnimacionEntrada(): void {
    this.limpiarAnimacionEntrada();
    this.animacionEntradaEnCurso = true;
    this.animacionEntradaTimeoutId = setTimeout(() => {
      this.animacionEntradaTimeoutId = null;
      this.animacionEntradaEnCurso = false;
      this.programarResizeGrafico();
    }, CHART_DASHBOARD_ANIMACION_ENTRADA_MS + 50);
  }

  private limpiarAnimacionEntrada(): void {
    if (this.animacionEntradaTimeoutId !== null) {
      clearTimeout(this.animacionEntradaTimeoutId);
      this.animacionEntradaTimeoutId = null;
    }
    this.animacionEntradaEnCurso = false;
  }

  private programarResizeGrafico(reiniciarCacheTamano = false): void {
    if (this.animacionEntradaEnCurso) {
      return;
    }

    if (reiniciarCacheTamano) {
      this.ultimoTamanoContenedor = null;
    }

    if (this.resizeFrameId !== null) {
      cancelAnimationFrame(this.resizeFrameId);
    }
    this.resizeFrameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.resizeFrameId = null;
        this.grafico?.resize();
      });
    });
  }

  private destruirGrafico(): void {
    this.limpiarAnimacionEntrada();

    if (this.resizeFrameId !== null) {
      cancelAnimationFrame(this.resizeFrameId);
      this.resizeFrameId = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.ultimoTamanoContenedor = null;
    this.graficoListo = false;
    if (this.grafico) {
      this.grafico.destroy();
      this.grafico = null;
    }
  }
}
